import React, { useMemo } from 'react';
import { Bar, Line, Scatter } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    LineElement,
    PointElement,
    LinearScale,
    CategoryScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { Box, Grid, Paper, Typography } from '@mui/material';

ChartJS.register(LineElement, PointElement, LinearScale, CategoryScale, BarElement, Title, Tooltip, Legend);

// ---------------------------------------------------------------------------
// Colour palette for operators (up to 10)
// ---------------------------------------------------------------------------

const OP_COLORS = [
    '#90caf9', // blue
    '#a5d6a7', // green
    '#ffb74d', // amber
    '#f48fb1', // pink
    '#ce93d8', // purple
    '#80deea', // cyan
    '#fff176', // yellow
    '#ff8a65', // orange
    '#b0bec5', // grey-blue
    '#ef9a9a', // red
];

function opColor(idx) { return OP_COLORS[idx % OP_COLORS.length]; }

/** Convert a hex color to rgba with given alpha */
function hexToRgba(hex, alpha) {
    const h = hex.replace('#', '');
    const r = parseInt(h.substring(0, 2), 16);
    const g = parseInt(h.substring(2, 4), 16);
    const b = parseInt(h.substring(4, 6), 16);
    return `rgba(${r},${g},${b},${alpha})`;
}

// ---------------------------------------------------------------------------
// Common chart options factory
// ---------------------------------------------------------------------------

function baseOptions(xLabel, yLabel, extraPlugins = {}) {
    return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'bottom',
                labels: { color: 'rgba(255,255,255,0.7)', boxWidth: 12, font: { size: 11 } },
            },
            tooltip: { enabled: true },
            ...extraPlugins,
        },
        scales: {
            x: {
                title: { display: !!xLabel, text: xLabel, color: 'rgba(255,255,255,0.55)', font: { size: 11 } },
                ticks: { color: 'rgba(255,255,255,0.6)', font: { size: 10 } },
                grid:  { color: 'rgba(255,255,255,0.06)' },
            },
            y: {
                title: { display: !!yLabel, text: yLabel, color: 'rgba(255,255,255,0.55)', font: { size: 11 } },
                ticks: { color: 'rgba(255,255,255,0.6)', font: { size: 10 } },
                grid:  { color: 'rgba(255,255,255,0.06)' },
            },
        },
    };
}

// ---------------------------------------------------------------------------
// Chart wrapper
// ---------------------------------------------------------------------------

function ChartCard({ title, children, height = 280 }) {
    return (
        <Paper sx={{ p: 2, backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 1, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: 'rgba(255,255,255,0.85)' }}>
                {title}
            </Typography>
            <Box sx={{ height, position: 'relative' }}>
                {children}
            </Box>
        </Paper>
    );
}

// ---------------------------------------------------------------------------
// Chart 1: Variance Components %Contribution
// ---------------------------------------------------------------------------

function VarianceContribChart({ varianceComponents }) {
    const data = useMemo(() => {
        if (!varianceComponents?.length) return null;

        // Source display order and labels
        const ORDER = ['Repeatability', 'Reproducibility', 'Operator', 'Operator × Part', 'Part-to-Part', 'Total GRR'];
        const rows = varianceComponents.filter(r => ORDER.includes(r.source) && r.pctContribution !== null);

        const labels = rows.map(r => r.source);
        const pcts   = rows.map(r => r.pctContribution ?? 0);
        const bgColors = rows.map(r => {
            if (r.source === 'Total GRR') {
                if (r.pctContribution < 10)  return 'rgba(129,199,132,0.7)';
                if (r.pctContribution <= 30) return 'rgba(255,183,77,0.7)';
                return 'rgba(229,115,115,0.7)';
            }
            return 'rgba(144,202,249,0.7)';
        });
        const borderColors = bgColors.map(c => c.replace('0.7)', '1)'));

        return {
            labels,
            datasets: [{
                label: '% Contribution',
                data: pcts,
                backgroundColor: bgColors,
                borderColor: borderColors,
                borderWidth: 1,
            }],
        };
    }, [varianceComponents]);

    if (!data) return <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.4)' }}>No data</Typography>;

    const options = {
        ...baseOptions('Source', '% Contribution to Total Variance'),
        scales: {
            ...baseOptions('Source', '% Contribution to Total Variance').scales,
            y: {
                ...baseOptions('', '').scales.y,
                title: { display: true, text: '% Contribution', color: 'rgba(255,255,255,0.55)', font: { size: 11 } },
                min: 0,
                max: 100,
            },
        },
    };

    return (
        <ChartCard title="Variance Components — % Contribution">
            <Bar data={data} options={options} />
        </ChartCard>
    );
}

// ---------------------------------------------------------------------------
// Chart 2: Operator × Part Interaction Plot
// ---------------------------------------------------------------------------

function InteractionPlot({ measurements }) {
    const { data } = useMemo(() => {
        if (!measurements?.length) return { data: null, operators: [] };

        const parts     = [...new Set(measurements.map(m => m.part))].sort((a, b) => a - b);
        const operators = [...new Set(measurements.map(m => m.operator))].sort();

        // For each operator, compute mean per part
        const datasets = operators.map((op, oi) => {
            const pts = parts.map(pt => {
                const vals = measurements.filter(m => m.operator === op && m.part === pt).map(m => m.value);
                return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
            });
            return {
                label: op,
                data: pts,
                borderColor: opColor(oi),
                backgroundColor: opColor(oi),
                pointRadius: 5,
                pointHoverRadius: 7,
                tension: 0.2,
                fill: false,
            };
        });

        return {
            data: { labels: parts.map(p => `Part ${p}`), datasets },
            operators,
        };
    }, [measurements]);

    if (!data) return null;

    return (
        <ChartCard title="Operator × Part Interaction Plot">
            <Line data={data} options={baseOptions('Part', 'Mean Measurement')} />
        </ChartCard>
    );
}

// ---------------------------------------------------------------------------
// Chart 3: Measurement by Operator (means bar + individual points scatter)
// ---------------------------------------------------------------------------

function MeasurementByOperatorChart({ measurements }) {
    const data = useMemo(() => {
        if (!measurements?.length) return null;

        const operators = [...new Set(measurements.map(m => m.operator))].sort();

        // Bar dataset: mean per operator
        const means = operators.map(op => {
            const vals = measurements.filter(m => m.operator === op).map(m => m.value);
            return vals.reduce((a, b) => a + b, 0) / vals.length;
        });

        // Scatter datasets: one per operator (individual points)
        // Use operator label string as x value to align with category axis
        const scatterSets = operators.map((op, oi) => {
            const vals = measurements.filter(m => m.operator === op).map(m => m.value);
            return {
                type: 'scatter',
                label: `${op} pts`,
                data: vals.map(v => ({ x: op, y: v })),
                backgroundColor: hexToRgba(opColor(oi), 0.5),
                pointRadius: 4,
                showLine: false,
            };
        });

        return {
            labels: operators,
            datasets: [
                {
                    type: 'bar',
                    label: 'Mean',
                    data: means,
                    backgroundColor: operators.map((_, i) => hexToRgba(opColor(i), 0.35)),
                    borderColor: operators.map((_, i) => opColor(i)),
                    borderWidth: 1.5,
                    categoryPercentage: 0.5,
                    barPercentage: 0.8,
                },
                ...scatterSets,
            ],
        };
    }, [measurements]);

    if (!data) return null;

    const options = {
        ...baseOptions('Operator', 'Measurement Value'),
        scales: {
            x: {
                ...baseOptions('Operator', '').scales.x,
                type: 'category',
            },
            y: {
                ...baseOptions('', 'Measurement Value').scales.y,
            },
        },
        plugins: {
            ...baseOptions('', '').plugins,
            legend: {
                display: false,
            },
        },
    };

    return (
        <ChartCard title="Measurements by Operator">
            <Bar data={data} options={options} />
        </ChartCard>
    );
}

// ---------------------------------------------------------------------------
// Chart 4: Run Chart by Operator
// ---------------------------------------------------------------------------

function RunChart({ measurements }) {
    const data = useMemo(() => {
        if (!measurements?.length) return null;

        const operators = [...new Set(measurements.map(m => m.operator))].sort();

        // One dataset per operator, preserving sequence within operator
        const datasets = operators.map((op, oi) => {
            const opMeasurements = measurements
                .map((m, idx) => ({ ...m, seq: idx }))
                .filter(m => m.operator === op);

            return {
                label: op,
                data: opMeasurements.map(m => ({ x: m.seq + 1, y: m.value })),
                borderColor: opColor(oi),
                backgroundColor: opColor(oi),
                pointRadius: 4,
                pointHoverRadius: 6,
                tension: 0,
                showLine: false,
            };
        });

        return { datasets };
    }, [measurements]);

    if (!data) return null;

    const options = {
        ...baseOptions('Measurement Sequence', 'Value'),
        scales: {
            x: {
                ...baseOptions('Measurement Sequence', '').scales.x,
                type: 'linear',
                title: { display: true, text: 'Measurement Sequence', color: 'rgba(255,255,255,0.55)', font: { size: 11 } },
            },
            y: {
                ...baseOptions('', 'Value').scales.y,
            },
        },
    };

    return (
        <ChartCard title="Run Chart by Operator">
            <Scatter data={data} options={options} />
        </ChartCard>
    );
}

// ---------------------------------------------------------------------------
// Main export
// ---------------------------------------------------------------------------

function GRRChartSection({ results, measurements }) {
    if (!results) return null;

    const { varianceComponents } = results;

    return (
        <Box>
            <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                    <VarianceContribChart varianceComponents={varianceComponents} />
                </Grid>
                <Grid item xs={12} md={6}>
                    <InteractionPlot measurements={measurements} />
                </Grid>
                <Grid item xs={12} md={6}>
                    <MeasurementByOperatorChart measurements={measurements} />
                </Grid>
                <Grid item xs={12} md={6}>
                    <RunChart measurements={measurements} />
                </Grid>
            </Grid>
        </Box>
    );
}

export default GRRChartSection;
